import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Fab,
  Button
} from '@mui/material';
import {
  Person,
  Phone,
  LocationOn,
  CalendarToday,
  FamilyRestroom,
  Psychology,
  Favorite,
  Print
} from '@mui/icons-material';
import { clienteService } from '../../services/api';

interface Cliente {
  id: number;
  nome: string;
  cpf?: string;
  email?: string;
  celular?: string;
  telefone?: string;
  dataNascimento?: string;
  genero?: string;
  estadoCivil?: string;
  profissao?: string;
  escolaridade?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  cep?: string;
  uf?: string;
  diaAtendimento?: string;
  horaAtendimento?: string;
  valorAtendimento?: number;
  tipoCliente?: string;
  foto?: string;
  nomePai?: string;
  nomeMae?: string;
  nomeConjuge?: string;
  profissaoConjuge?: string;
  telefoneConjuge?: string;
  // Anamnese - seguindo ordem do formulário
  queixaPrincipal?: string;
  mudancasPeriodo?: string;
  sintomasSinais?: string;
  acontecimentoMarcante?: string;
  relacionamentoFamiliar?: string;
  // Histórico clínico (campos booleanos)
  tentativaSuicidioSn?: boolean;
  tentativaSuicidioDetalhes?: string;
  tratamentoPsicologicoSn?: boolean;
  tratamentoPsicologicoDetalhes?: string;
  algumaDoencaSn?: boolean;
  algumaDoencaDetalhes?: string;
  usoMedicamentosSn?: boolean;
  usoMedicamentosDetalhes?: string;
  familiarDoencaMentalSn?: boolean;
  familiarDoencaMentalDetalhes?: string;
  abusoAlcoolSn?: boolean;
  abusoAlcoolDetalhes?: string;
  // Observações gerais
  observacoesGerais?: string;
  // Campos legados (manter para compatibilidade)
  historiaDoenca?: string;
  historicoFamiliar?: string;
  medicamentosUso?: string;
  alergias?: string;
  cirurgias?: string;
  hospitalizacoes?: string;
}

const ClienteFichaPrint: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper para normalizar valores vindos do backend para booleano real
  const toBoolean = (valor: any): boolean | undefined => {
    if (valor === undefined || valor === null) return undefined;
    if (typeof valor === 'boolean') return valor;
    if (typeof valor === 'number') return valor === 1;
    
    // Tratar Buffer do MySQL (campos BIT/TINYINT)
    if (valor && typeof valor === 'object' && valor.type === 'Buffer' && Array.isArray(valor.data)) {
      const bufferValue = valor.data[0] || 0;
      return bufferValue === 1;
    }
    
    if (typeof valor === 'string') {
      const s = valor.trim().toLowerCase();
      if (['1', 'true', 't', 'sim', 's', 'yes', 'y'].includes(s)) return true;
      if (['0', 'false', 'f', 'nao', 'não', 'n', 'no'].includes(s)) return false;
    }
    return !!valor;
  };

  useEffect(() => {
    const carregarCliente = async () => {
      if (!id) {
        setError('ID do cliente não fornecido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('🔍 [ClienteFichaPrint] Iniciando carregamento do cliente ID:', id);
        console.log('🔍 [ClienteFichaPrint] URL da API:', `https://trajetus.net/api/clientes/${id}`);
        
        // Adicionar um pequeno delay para garantir que a API esteja pronta
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const response = await clienteService.buscarPorId(parseInt(id));
        console.log('📋 [ClienteFichaPrint] Resposta completa da API:', response);
        console.log('📋 [ClienteFichaPrint] Dados do cliente (response.data):', response.data);
        console.log('📋 [ClienteFichaPrint] Tipo de response.data:', typeof response.data);
        console.log('📋 [ClienteFichaPrint] Keys de response.data:', Object.keys(response.data || {}));
        
        // Tentar diferentes estruturas de resposta
        let clienteData = null;
        
        // Estrutura 1: response.data diretamente
        if (response.data && response.data.nome) {
          clienteData = response.data;
          console.log('✅ [ClienteFichaPrint] Usando estrutura response.data diretamente');
        }
        // Estrutura 2: response.data.data
        else if (response.data && response.data.data && response.data.data.nome) {
          clienteData = response.data.data;
          console.log('✅ [ClienteFichaPrint] Usando estrutura response.data.data');
        }
        // Estrutura 3: response.data.cliente
        else if (response.data && response.data.cliente && response.data.cliente.nome) {
          clienteData = response.data.cliente;
          console.log('✅ [ClienteFichaPrint] Usando estrutura response.data.cliente');
        }
        // Estrutura 4: response.data é um array
        else if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          clienteData = response.data[0];
          console.log('✅ [ClienteFichaPrint] Usando primeiro item do array response.data');
        }
        else {
          console.error('❌ [ClienteFichaPrint] Estrutura de dados não reconhecida:', {
            response: response,
            responseData: response.data,
            responseDataType: typeof response.data,
            responseDataKeys: response.data ? Object.keys(response.data) : 'N/A'
          });
          throw new Error('Estrutura de dados do cliente não reconhecida');
        }
        
        if (clienteData && clienteData.nome) {
          console.log('🔍 [ClienteFichaPrint] Valores RAW do backend ANTES da normalização:', {
            tentativa_suicidio_sn: clienteData.tentativa_suicidio_sn, tipo: typeof clienteData.tentativa_suicidio_sn,
            tratamento_psicologico_sn: clienteData.tratamento_psicologico_sn, tipo2: typeof clienteData.tratamento_psicologico_sn,
            alguma_doenca_sn: clienteData.alguma_doenca_sn, tipo3: typeof clienteData.alguma_doenca_sn,
            uso_medicamentos_sn: clienteData.uso_medicamentos_sn, tipo4: typeof clienteData.uso_medicamentos_sn,
            familiar_doenca_mental_sn: clienteData.familiar_doenca_mental_sn, tipo5: typeof clienteData.familiar_doenca_mental_sn,
            abuso_alcool_sn: clienteData.abuso_alcool_sn, tipo6: typeof clienteData.abuso_alcool_sn
          });
          // Mapear campos do backend (underscore) para frontend (camelCase)
          const clienteMapeado = {
            ...clienteData,
            // Mapear campos com underscore
            foto: clienteData.foto, // Garantir que a foto seja mapeada
            dataNascimento: clienteData.data_nascimento || clienteData.dataNascimento,
            diaAtendimento: clienteData.dia_atendimento || clienteData.diaAtendimento,
            horaAtendimento: clienteData.hora_atendimento || clienteData.horaAtendimento,
            valorAtendimento: clienteData.valor_atendimento || clienteData.valorAtendimento,
            tipoCliente: clienteData.tipo_cliente_id || clienteData.tipo_cliente || clienteData.tipoCliente,
            estadoCivil: clienteData.estado_civil_id || clienteData.estado_civil || clienteData.estadoCivil,
            genero: clienteData.genero_id || clienteData.genero,
            escolaridade: clienteData.escolaridade_id || clienteData.escolaridade,
            nomePai: clienteData.nome_pai || clienteData.nomePai,
            nomeMae: clienteData.nome_mae || clienteData.nomeMae,
            nomeConjuge: clienteData.nome_conjuge || clienteData.nomeConjuge,
            profissaoConjuge: clienteData.profissao_conjuge || clienteData.profissaoConjuge,
            telefoneConjuge: clienteData.telefone_conjuge || clienteData.telefoneConjuge,
            // Mapear campos de anamnese seguindo a ordem exata do formulário
            queixaPrincipal: clienteData.queixa_principal || clienteData.queixaPrincipal,
            mudancasPeriodo: clienteData.mudancas_periodo || clienteData.mudancasPeriodo,
            sintomasSinais: clienteData.sintomas_sinais || clienteData.sintomasSinais,
            acontecimentoMarcante: clienteData.acontecimento_marcante || clienteData.acontecimentoMarcante,
            relacionamentoFamiliar: clienteData.relacionamento_familiar || clienteData.relacionamentoFamiliar,
            // Campos varchar da anamnese
            alergias: clienteData.alergias,
            cirurgias: clienteData.cirurgias,
            hospitalizacoes: clienteData.hospitalizacoes,
            // Campos booleanos de histórico clínico (sem duplicatas)
            tentativaSuicidioSn: toBoolean(clienteData.tentativa_suicidio_sn ?? clienteData.tentativaSuicidioSn),
            tentativaSuicidioDetalhes: clienteData.tentativa_suicidio || clienteData.tentativaSuicidioDetalhes,
            tratamentoPsicologicoSn: toBoolean(clienteData.tratamento_psicologico_sn ?? clienteData.tratamentoPsicologicoSn),
            tratamentoPsicologicoDetalhes: clienteData.tratamento_psicologico || clienteData.tratamentoPsicologicoDetalhes,
            algumaDoencaSn: toBoolean(clienteData.alguma_doenca_sn ?? clienteData.algumaDoencaSn),
            algumaDoencaDetalhes: clienteData.alguma_doenca || clienteData.algumaDoencaDetalhes,
            usoMedicamentosSn: toBoolean(clienteData.uso_medicamentos_sn ?? clienteData.usoMedicamentosSn),
            usoMedicamentosDetalhes: clienteData.uso_medicamento || clienteData.usoMedicamentosDetalhes,
            familiarDoencaMentalSn: toBoolean(clienteData.familiar_doenca_mental_sn ?? clienteData.familiarDoencaMentalSn),
            familiarDoencaMentalDetalhes: clienteData.familiar_doenca_mental || clienteData.familiarDoencaMentalDetalhes,
            abusoAlcoolSn: toBoolean(clienteData.abuso_alcool_sn ?? clienteData.abusoAlcoolSn),
            abusoAlcoolDetalhes: clienteData.abuso_alcool || clienteData.abusoAlcoolDetalhes,
            // Observações gerais
            observacoesGerais: clienteData.obs_geral || clienteData.observacoesGerais
          };
          
                    console.log('🧪 [ClienteFichaPrint] Booleans normalizados:', {
            tentativaSuicidioSn: clienteMapeado.tentativaSuicidioSn, tipo_tentativa: typeof clienteMapeado.tentativaSuicidioSn,
            tratamentoPsicologicoSn: clienteMapeado.tratamentoPsicologicoSn, tipo_trat: typeof clienteMapeado.tratamentoPsicologicoSn,
            algumaDoencaSn: clienteMapeado.algumaDoencaSn, tipo_doenca: typeof clienteMapeado.algumaDoencaSn,
            usoMedicamentosSn: clienteMapeado.usoMedicamentosSn, tipo_meds: typeof clienteMapeado.usoMedicamentosSn,
            familiarDoencaMentalSn: clienteMapeado.familiarDoencaMentalSn, tipo_fam: typeof clienteMapeado.familiarDoencaMentalSn,
            abusoAlcoolSn: clienteMapeado.abusoAlcoolSn, tipo_abuso: typeof clienteMapeado.abusoAlcoolSn,
          });
          setCliente(clienteMapeado);
          setError(null);
          console.log('✅ [ClienteFichaPrint] Cliente carregado com sucesso:', clienteMapeado.nome);
          console.log('📋 [ClienteFichaPrint] Dados completos do cliente:', clienteMapeado);
        } else {
          console.error('❌ [ClienteFichaPrint] Dados do cliente inválidos:', clienteData);
          throw new Error('Dados do cliente não encontrados ou inválidos na resposta');
        }
        
      } catch (err: any) {
        console.error('❌ [ClienteFichaPrint] Erro detalhado ao carregar cliente:', {
          error: err,
          message: err.message,
          response: err.response,
          status: err.response?.status,
          data: err.response?.data
        });
        setError(`Erro ao carregar dados do cliente: ${err.response?.data?.message || err.message || 'Erro desconhecido'}`);
      } finally {
        setLoading(false);
      }
    };

    carregarCliente();
  }, [id]);

  const handleImprimir = () => {
    window.print();
  };

  const formatarData = (data: string | undefined): string => {
    if (!data) return 'Não informado';
    try {
      const date = new Date(data);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return data;
    }
  };

  const formatarValor = (valor: number | undefined): string => {
    if (!valor) return 'Não informado';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarTextoBooleano = (valor: any, detalhes?: string): string => {
  const normalizado = toBoolean(valor);
  if (normalizado === undefined || normalizado === null) return 'Não informado';
  const resposta = normalizado ? 'Sim' : 'Não';
  return detalhes && normalizado ? `${resposta} - ${detalhes}` : resposta;
};

  // Função para mapear ID do gênero para texto
  const mapearGenero = (generoId: number | string | undefined): string => {
    if (!generoId) return 'Não informado';
    const generos: { [key: string]: string } = {
      '1': 'Masculino',
      '2': 'Feminino', 
      '3': 'Outro'
    };
    return generos[String(generoId)] || 'Não informado';
  };

  // Função para mapear ID do estado civil para texto
  const mapearEstadoCivil = (estadoCivilId: number | string | undefined): string => {
    if (!estadoCivilId) return 'Não informado';
    const estadosCivis: { [key: string]: string } = {
      '1': 'Casado(a)',
      '2': 'Separado(a)',
      '3': 'Viúvo(a)',
      '4': 'Solteiro(a)',
      '5': 'Namorando',
      '6': 'Divorciado(a)',
      '7': 'União Estável'
    };
    return estadosCivis[String(estadoCivilId)] || 'Não informado';
  };

  // Função para mapear ID da escolaridade para texto
  const mapearEscolaridade = (escolaridadeId: number | string | undefined): string => {
    if (!escolaridadeId) return 'Não informado';
    const escolaridades: { [key: string]: string } = {
      '1': 'Ensino Fundamental Incompleto',
      '2': 'Ensino Fundamental Completo',
      '3': 'Ensino Médio Incompleto',
      '4': 'Ensino Médio Completo',
      '5': 'Ensino Superior Incompleto',
      '6': 'Ensino Superior Completo',
      '7': 'Pós-graduação',
      '8': 'Mestrado',
      '9': 'Doutorado'
    };
    return escolaridades[String(escolaridadeId)] || 'Não informado';
  };

  // Função para mapear ID do tipo de cliente para texto
  const mapearTipoCliente = (tipoClienteId: number | string | undefined): string => {
    if (!tipoClienteId) return 'Não informado';
    const tiposCliente: { [key: string]: string } = {
      '1': 'Paciente',
      '2': 'Aluno',
      '3': 'Atendimento Social',
      '4': 'Social'
    };
    return tiposCliente[String(tipoClienteId)] || 'Não informado';
  };

  // Aguardar carregamento completo antes de renderizar
  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Carregando dados do cliente...</Typography>
        <Typography variant="body2" color="text.secondary">
          Cliente ID: {id}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Aguarde, estamos buscando as informações no banco de dados...
        </Typography>
      </Box>
    );
  }

  if (error || !cliente) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert severity="error" sx={{ mb: 2, maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>Erro ao carregar cliente</Typography>
          <Typography variant="body2">
            {error || 'Cliente não encontrado'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Cliente ID: {id}
          </Typography>
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Tentar Novamente
        </Button>
      </Box>
    );
  }

  // Verificação adicional para garantir que temos dados mínimos
  if (!cliente || (!cliente.nome && !cliente.id)) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert severity="warning" sx={{ mb: 2, maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>Dados incompletos</Typography>
          <Typography variant="body2">
            Os dados do cliente não foram carregados completamente. Aguarde um momento e tente novamente.
          </Typography>
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Recarregar Página
        </Button>
      </Box>
    );
  }

  console.log('🎯 [ClienteFichaPrint] Renderizando template com cliente:', {
    id: cliente.id,
    nome: cliente.nome,
    email: cliente.email,
    celular: cliente.celular
  });
  return (
    <>
      {/* Botão Flutuante Imprimir */}
      <Fab
        color="primary"
        aria-label="imprimir"
        onClick={handleImprimir}
        className="no-print"
        sx={{
          position: 'fixed',
          top: 80, // Alterado de 20 para 80 (baixou 60px)
          right: 20,
          zIndex: 1000,
          '@media print': {
            display: 'none'
          }
        }}
      >
        <Print />
      </Fab>

      <Box sx={{ maxWidth: '210mm', margin: '0 auto', p: 2, backgroundColor: '#fff' }}>
        <Paper elevation={0} sx={{ p: 3 }}>
          {/* Cabeçalho */}
          <Box textAlign="center" mb={3}>
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              FICHA DO CLIENTE
            </Typography>
            <Divider sx={{ my: 2 }} />
          </Box>

          {/* Informações Principais */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Person sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h5" component="h2" color="primary">
                  DADOS PESSOAIS
                </Typography>
              </Box>
              
              {/* Adicionar foto do cliente */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3} sx={{ textAlign: 'center', mb: 2 }}>
                  <Avatar
                    src={cliente?.foto}
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      mx: 'auto', 
                      mb: 1,
                      border: '2px solid',
                      borderColor: 'primary.main'
                    }}
                  >
                    <Person sx={{ fontSize: 60 }} />
                  </Avatar>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Nome Completo:</Typography>
                      <Typography variant="body1">{cliente?.nome || 'Não informado'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">CPF:</Typography>
                      <Typography variant="body1">{cliente?.cpf || 'Não informado'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">E-mail:</Typography>
                      <Typography variant="body1">{cliente?.email || 'Não informado'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Telefone:</Typography>
                      <Typography variant="body1">{cliente?.telefone || cliente?.celular || 'Não informado'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Data de Nascimento:</Typography>
                      <Typography variant="body1">{formatarData(cliente?.dataNascimento)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Gênero:</Typography>
                      <Typography variant="body1">{mapearGenero(cliente?.genero)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Estado Civil:</Typography>
                      <Typography variant="body1">{mapearEstadoCivil(cliente?.estadoCivil)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Profissão:</Typography>
                      <Typography variant="body1">{cliente?.profissao || 'Não informado'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Escolaridade:</Typography>
                      <Typography variant="body1">{mapearEscolaridade(cliente?.escolaridade)}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Endereço */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <LocationOn sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h5" component="h2" color="primary">
                  ENDEREÇO
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <Typography variant="body2" color="text.secondary">Logradouro:</Typography>
                  <Typography variant="body1">{cliente?.endereco || 'Não informado'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Número:</Typography>
                  <Typography variant="body1">{cliente?.numero || 'Não informado'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Complemento:</Typography>
                  <Typography variant="body1">{cliente?.complemento || 'Não informado'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Bairro:</Typography>
                  <Typography variant="body1">{cliente?.bairro || 'Não informado'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Cidade:</Typography>
                  <Typography variant="body1">{cliente?.cidade || 'Não informado'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">UF:</Typography>
                  <Typography variant="body1">{cliente?.uf || 'Não informado'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">CEP:</Typography>
                  <Typography variant="body1">{cliente?.cep || 'Não informado'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Atendimento */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <CalendarToday sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h5" component="h2" color="primary">
                  ATENDIMENTO
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Dia de Atendimento:</Typography>
                  <Typography variant="body1">{cliente?.diaAtendimento || 'Não informado'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Hora de Atendimento:</Typography>
                  <Typography variant="body1">{cliente?.horaAtendimento || 'Não informado'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Valor do Atendimento:</Typography>
                  <Typography variant="body1">{formatarValor(cliente?.valorAtendimento)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Tipo de Cliente:</Typography>
                  <Typography variant="body1">{mapearTipoCliente(cliente?.tipoCliente)}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Filiação */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <FamilyRestroom sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h5" component="h2" color="primary">
                  FILIAÇÃO
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Nome do Pai:</Typography>
                  <Typography variant="body1">{cliente?.nomePai || 'Não informado'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Nome da Mãe:</Typography>
                  <Typography variant="body1">{cliente?.nomeMae || 'Não informado'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Cônjuge */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Favorite sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h5" component="h2" color="primary">
                  CÔNJUGE
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Nome do Cônjuge:</Typography>
                  <Typography variant="body1">{cliente?.nomeConjuge || 'Não informado'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Profissão do Cônjuge:</Typography>
                  <Typography variant="body1">{cliente?.profissaoConjuge || 'Não informado'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Telefone do Cônjuge:</Typography>
                  <Typography variant="body1">{cliente?.telefoneConjuge || 'Não informado'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Anamnese */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Psychology sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h5" component="h2" color="primary">
                  ANAMNESE
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                {/* 1. Queixa Principal */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Queixa Principal:</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                    {cliente?.queixaPrincipal || 'Não informado'}
                  </Typography>
                </Grid>
                
                {/* 2. Mudanças no Período */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Mudanças no Período:</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                    {cliente?.mudancasPeriodo || 'Não informado'}
                  </Typography>
                </Grid>
                
                {/* 3. Sintomas e Sinais */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Sintomas e Sinais:</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                    {cliente?.sintomasSinais || 'Não informado'}
                  </Typography>
                </Grid>
                
                {/* 4. Acontecimento Marcante */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Acontecimento Marcante:</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                    {cliente?.acontecimentoMarcante || 'Não informado'}
                  </Typography>
                </Grid>
                
                {/* 5. Relacionamento Familiar */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Relacionamento Familiar:</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                    {cliente?.relacionamentoFamiliar || 'Não informado'}
                  </Typography>
                </Grid>
                
                {/* Histórico Clínico */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3, mb: 2 }}>
                    Histórico Clínico
                  </Typography>
                </Grid>
                
                {/* 6. Tentativa de Suicídio */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Já teve tentativa de suicídio?</Typography>
                  <Typography variant="body1">{formatarTextoBooleano(cliente?.tentativaSuicidioSn)}</Typography>
                  {toBoolean(cliente?.tentativaSuicidioSn) && cliente?.tentativaSuicidioDetalhes && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', pl: 2 }}>
                      Detalhes: {cliente.tentativaSuicidioDetalhes}
                    </Typography>
                  )}
                </Grid>
                
                {/* 7. Tratamento Psicológico */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Já fez tratamento psicológico anteriormente?</Typography>
                  <Typography variant="body1">{formatarTextoBooleano(cliente?.tratamentoPsicologicoSn)}</Typography>
                  {toBoolean(cliente?.tratamentoPsicologicoSn) && cliente?.tratamentoPsicologicoDetalhes && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', pl: 2 }}>
                      Detalhes: {cliente.tratamentoPsicologicoDetalhes}
                    </Typography>
                  )}
                </Grid>
                
                {/* 8. Alguma Doença */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Possui alguma doença?</Typography>
                  <Typography variant="body1">{formatarTextoBooleano(cliente?.algumaDoencaSn)}</Typography>
                  {toBoolean(cliente?.algumaDoencaSn) && cliente?.algumaDoencaDetalhes && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', pl: 2 }}>
                      Detalhes: {cliente.algumaDoencaDetalhes}
                    </Typography>
                  )}
                </Grid>
                
                {/* 9. Uso de Medicamentos */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Faz uso de medicamentos?</Typography>
                  <Typography variant="body1">{formatarTextoBooleano(cliente?.usoMedicamentosSn)}</Typography>
                  {toBoolean(cliente?.usoMedicamentosSn) && cliente?.usoMedicamentosDetalhes && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', pl: 2 }}>
                      Detalhes: {cliente.usoMedicamentosDetalhes}
                    </Typography>
                  )}
                </Grid>
                
                {/* 10. Histórico Familiar de Doença Mental */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Há histórico de doença mental na família?</Typography>
                  <Typography variant="body1">{formatarTextoBooleano(cliente?.familiarDoencaMentalSn)}</Typography>
                  {toBoolean(cliente?.familiarDoencaMentalSn) && cliente?.familiarDoencaMentalDetalhes && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', pl: 2 }}>
                      Detalhes: {cliente.familiarDoencaMentalDetalhes}
                    </Typography>
                  )}
                </Grid>
                
                {/* 11. Abuso de Álcool */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Faz uso abusivo de álcool ou outras substâncias?</Typography>
                  <Typography variant="body1">{formatarTextoBooleano(cliente?.abusoAlcoolSn)}</Typography>
                  {toBoolean(cliente?.abusoAlcoolSn) && cliente?.abusoAlcoolDetalhes && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', pl: 2 }}>
                      Detalhes: {cliente.abusoAlcoolDetalhes}
                    </Typography>
                  )}
                </Grid>
                
                {/* 12. Observações Gerais */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Observações Gerais:</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                    {cliente?.observacoesGerais || 'Não informado'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          {/* Rodapé */}
          <Box textAlign="center" mt={4} pt={2} borderTop={1} borderColor="divider">
            <Typography variant="body2" color="text.secondary">
              Ficha gerada em: {new Date().toLocaleString('pt-BR')}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default ClienteFichaPrint;






